import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const PageContainer = styled.div`
  background-color: #000;
  min-height: 100vh;
  color: #fff;
  padding: 80px 2rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const BackLink = styled(Link)`
  position: fixed;
  top: 28px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 1rem;
  color: white;
  text-decoration: none;
  text-align: center;
  transition: transform 0.3s;
  &:hover {
    transform: translateX(-50%) scale(1.2);
  }
`;

const Content = styled.div`
  width: 100%;
  max-width: 700px;
`;

const Title = styled.h1`
  font-size: 2.5em;
  color: #fff;
  margin-bottom: 2.5rem;
`;

const Prose = styled.p`
  font-size: 1rem;
  color: #bbb;
  line-height: 1.85;
  margin: 0 0 1.6rem;
`;

function About() {
  return (
    <PageContainer>
      <BackLink to="/">↑</BackLink>
      <Content>
        <Title>About</Title>
        <Prose>
          I'm Avneet — an AI/ML engineer based in Tampa, Florida. I build systems that work under
          real-world constraints: edge compute with hard latency budgets, autonomous trading
          infrastructure that runs unsupervised, and LLM tooling that extends what AI assistants
          can do.
        </Prose>
        <Prose>
          My background is unusual for ML. I studied quantitative finance at Stevens Institute of
          Technology before pivoting to data science at NJIT for my master's. That finance
          foundation shows up in how I think about risk, evaluation, and decision-making under
          uncertainty — whether I'm designing a trading strategy or assessing an AI system's
          failure modes.
        </Prose>
        <Prose>
          Professionally, I work at the intersection of applied ML and defense systems. I've spent
          the last two years evaluating and integrating AI solutions for military applications —
          computer vision, edge inference, and platform integration work that operates under
          constraints most engineers never encounter. The details of that work are sensitive, but
          the dispositions it cultivates are not: adversarial thinking, rigorous evaluation, and
          an awareness that the cost of being wrong is sometimes unacceptably high.
        </Prose>
        <Prose>
          Outside of work, I build things. My current projects span autonomous trading (a momentum
          bot validated across 11 independent out-of-sample windows and now trading live), LLM
          security evaluation (systematically attacking AI agents to understand how they fail), and
          personal health infrastructure (a suite of tools that track my nutrition, workouts, and
          body composition through Claude's Model Context Protocol).
        </Prose>
        <Prose>
          I'm particularly interested in AI safety and evaluation — not as abstract concepts, but
          as engineering disciplines. How do you test whether an LLM agent will behave correctly
          when given tools and access to external data? How do you evaluate a system that can be
          manipulated through its own inputs? These questions feel urgent to me, and my background
          gives me a framework for thinking about them that most ML engineers don't have.
        </Prose>
        <Prose>
          I'm currently exploring AI safety, alignment research, and applied ML where evaluation
          rigor matters. If that intersects with what you're working on, I'd like to hear about it.
        </Prose>
        <Prose style={{fontSize: '0.9em', color: '#666', marginTop: '2rem'}}>
          Find me on{' '}
          <a href="https://medium.com/@neetium" target="_blank" rel="noopener noreferrer" style={{color: '#aaa', textDecoration: 'none'}}>Medium</a>
          {' · '}
          <a href="https://neetium.substack.com" target="_blank" rel="noopener noreferrer" style={{color: '#aaa', textDecoration: 'none'}}>Substack</a>
          {' · '}
          <a href="https://github.com/Asingh027" target="_blank" rel="noopener noreferrer" style={{color: '#aaa', textDecoration: 'none'}}>GitHub</a>
          {' · '}
          <a href="https://linkedin.com/in/avneetsinghurl" target="_blank" rel="noopener noreferrer" style={{color: '#aaa', textDecoration: 'none'}}>LinkedIn</a>
        </Prose>
      </Content>
    </PageContainer>
  );
}

export default About;
